// hooks/useAuth.js
// Auth Hook - Synchronized with Mobile App
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";

export const useAuth = () => {
  const queryClient = useQueryClient();
  const {
    user,
    token,
    isAuthenticated,
    isLoading: authLoading,
    sessionExpired,
    login: storeLogin,
    logout: storeLogout,
    updateUser,
    setLoading,
    clearSessionExpired,
  } = useAuthStore();

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials) => apiService.login(credentials),
    onSuccess: (data) => {
      if (data.status === "success" && data.data) {
        const { user, token, refreshToken } = data.data;
        storeLogin(user, token, refreshToken);
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.invalidateQueries({ queryKey: ["cart"] });
        queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      }
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (userData) => apiService.register(userData),
    onSuccess: (data) => {
      if (data.status === "success" && data.data) {
        // Some APIs auto-login after registration
        if (data.data.token) {
          const { user, token, refreshToken } = data.data;
          storeLogin(user, token, refreshToken);
        }
      }
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await apiService.logout();
      } catch (error) {
        // Ignore logout API errors
        console.log("Logout API error (ignored):", error);
      }
    },
    onSettled: () => {
      storeLogout(false);
      queryClient.clear();
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: (email) => apiService.forgotPassword(email),
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ email, code, newPassword }) =>
      apiService.resetPassword(email, code, newPassword),
  });

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: ({ code, email }) => apiService.verifyEmail({ code, email }),
  });

  // Resend verification mutation
  const resendVerificationMutation = useMutation({
    mutationFn: ({ email }) => apiService.resendVerification(email),
  });

  // Check email mutation
  const checkEmailMutation = useMutation({
    mutationFn: (email) => apiService.checkEmail(email),
  });

  // Social login mutation (Google, etc.)
  const socialLoginMutation = useMutation({
    mutationFn: ({ idToken, provider }) => apiService.socialLogin(idToken, provider),
    onSuccess: (data) => {
      if (data.status === "success" && data.data) {
        const { user, token, refreshToken } = data.data;
        storeLogin(user, token, refreshToken);
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.invalidateQueries({ queryKey: ["cart"] });
        queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      }
    },
  });

  // Firebase social login mutation (exchanges a Firebase ID token for our session)
  const firebaseSocialLoginMutation = useMutation({
    mutationFn: ({ firebaseIdToken, providerHint }) =>
      apiService.firebaseSocialLogin(firebaseIdToken, providerHint),
    onSuccess: (data) => {
      if (data.status === "success" && data.data) {
        const { user, token, refreshToken } = data.data;
        storeLogin(user, token, refreshToken);
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.invalidateQueries({ queryKey: ["cart"] });
        queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      }
    },
  });

  // Get profile query
  const profileQuery = useQuery({
    queryKey: ["user", "profile"],
    queryFn: () => apiService.getProfile(),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Keep auth store in sync with the latest profile from the API
  useEffect(() => {
    const profileData = profileQuery.data?.data;
    if (profileQuery.data?.status === "success" && profileData) {
      updateUser(profileData);
    }
  }, [profileQuery.data, updateUser]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (userData) => apiService.updateProfile(userData),
    onSuccess: (data) => {
      if (data.status === "success" && data.data) {
        updateUser(data.data);
        queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
      }
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ oldPassword, newPassword }) =>
      apiService.changePassword(oldPassword, newPassword),
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: () => apiService.deleteAccount(),
    onSuccess: () => {
      storeLogout(false);
      queryClient.clear();
    },
  });

  return {
    // State
    user,
    token,
    isAuthenticated,
    isLoading: authLoading || loginMutation.isPending || registerMutation.isPending || socialLoginMutation.isPending || firebaseSocialLoginMutation.isPending,
    sessionExpired,

    // Profile
    profile: profileQuery.data?.data,
    isLoadingProfile: profileQuery.isLoading,
    isProfileFetched: profileQuery.isFetched,
    isProfileError: profileQuery.isError,

    // Auth actions
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    socialLogin: socialLoginMutation.mutateAsync,
    firebaseSocialLogin: firebaseSocialLoginMutation.mutateAsync,
    logout: logoutMutation.mutate,
    forgotPassword: forgotPasswordMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    verifyEmail: verifyEmailMutation.mutateAsync,
    resendVerification: resendVerificationMutation.mutateAsync,
    checkEmail: checkEmailMutation.mutateAsync,

    // Profile actions
    updateProfile: updateProfileMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    deleteAccount: deleteAccountMutation.mutateAsync,

    // Helpers
    setLoading,
    clearSessionExpired,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isSendingResetEmail: forgotPasswordMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,
    isVerifyingEmail: verifyEmailMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,

    // Errors
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    socialLoginError: socialLoginMutation.error,
    firebaseSocialLoginError: firebaseSocialLoginMutation.error,
    forgotPasswordError: forgotPasswordMutation.error,
    resetPasswordError: resetPasswordMutation.error,
  };
};

export default useAuth;

